//External
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { format, parse } from "date-fns";
import { useSelector } from "react-redux";
import { useRouter } from "next/router";
//Internal
import DateRangePicker from "../date-range";
import IconArrowRight from "../icons/icons-arrow-right";
import IconCalendar from "../icons/icons-calendar";
import IconLocationPin from "../icons/icons-location-pin";
import LocationPopover, { LOCATION_PLACEHOLDER, getPickupAddress } from "../location-popover";
import TimeDropdown from "./TimeDropdown";
import { getBookableTimeOptions, pickBookableTime } from "utils/reservation";
import { get_cookie, set_cookie } from "utils";

/*******************
@purpose : "Apr 18, 2026" -> "Sat, Apr 18" for display only
@Parameter : {value}
@Author : INIC
******************/
const formatDisplayDate = (value) => {
  if (!value) return value;
  try {
    return format(parse(value, "MMM d, yyyy", new Date()), "EEE, MMM d");
  } catch (e) {
    return value;
  }
};

/*******************
@Purpose : Homepage hero reservation search bar (start/end date, time,
           location, Next). The search-results page uses its own separate
           toolbar component (components/search-results-toolbar) so changes
           there don't affect this one and vice versa.
@Author : INIC
******************/
function ReservationSearchBar() {
  const router = useRouter();
  const currentLocationId =
    useSelector((state) => state?.common?.currentLocationId) ||
    get_cookie("currentLocationId");

  const stateObj = [
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ];

  const [dateObj, setDateObj] = useState(stateObj);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("10:00");

  // Hide time slots that are already in the past (or inside the booking
  // lead time) for the picked dates, and move the selected times off them.
  const startTimeOptions = getBookableTimeOptions(startDate);
  const endTimeOptions = getBookableTimeOptions(
    endDate,
    startDate && startDate === endDate ? parseInt(startTime, 10) : null
  );
  const startTimeOptionsKey = startTimeOptions.map((option) => `${option.id}${option.disabled ? "x" : ""}`).join(",");
  const endTimeOptionsKey = endTimeOptions.map((option) => `${option.id}${option.disabled ? "x" : ""}`).join(",");

  useEffect(() => {
    const next = pickBookableTime(startTimeOptions, startTime);
    if (next !== startTime) setStartTime(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTimeOptionsKey, startTime]);

  useEffect(() => {
    const next = pickBookableTime(endTimeOptions, endTime);
    if (next !== endTime) setEndTime(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endTimeOptionsKey, endTime]);
  // Only one of the four pill popovers (calendar, location, start/end time)
  // can be open at once - they're all anchored on the same pill and would
  // otherwise overlap.
  const [activePopover, setActivePopover] = useState(null);
  const isShowCalendar = activePopover === "calendar";
  const isLocationOpen = activePopover === "location";
  // Pickup is a fixed location (see LocationPopover) - pre-populate the
  // field with it instead of a "Location" placeholder the user would
  // otherwise have to open the popover and hit Save on to get to.
  const [locationDisplay, setLocationDisplay] = useState(getPickupAddress);
  const [savedLocationDraft, setSavedLocationDraft] = useState(null);
  const locationFieldRef = useRef();
  const searchBarRef = useRef();
  // Calendar/location popovers open above the pill by default, but that
  // renders off-screen once the page is scrolled and the search bar sits
  // near the top of the viewport - so flip to opening below it in that
  // case, same idea as TimeDropdown's own placement logic.
  const [popoverPlacement, setPopoverPlacement] = useState("above");
  const isLocationSelected = Boolean(locationDisplay) && locationDisplay !== "Location";
  const isFormComplete =
    Boolean(startDate) &&
    Boolean(endDate) &&
    Boolean(startTime) &&
    Boolean(endTime) &&
    isLocationSelected;

  /*******************
  @purpose : open the named popover, closing whichever of the other three
             (calendar / location / start time / end time) was open
  @Parameter : {name}
  @Author : INIC
  ******************/
  const togglePopover = (name) => {
    setActivePopover((prev) => (prev === name ? null : name));
  };

  /*******************
  @purpose : close whichever popover is currently open
  @Author : INIC
  ******************/
  const closePopover = () => setActivePopover(null);

  const handleClickOpenCalendar = () => togglePopover("calendar");
  const handleToggleLocation = () => togglePopover("location");

  /*******************
  @purpose : decide whether the calendar/location popovers open above or
             below the pill - below when the pill sits in the upper half of
             the viewport, so the dialog doesn't render above the top of the
             page once scrolled
  @Author : INIC
  ******************/
  useLayoutEffect(() => {
    if ((!isShowCalendar && !isLocationOpen) || !searchBarRef.current) return;

    const pillRect = searchBarRef.current.getBoundingClientRect();
    const isInLowerHalf = pillRect.top > window.innerHeight / 2;
    setPopoverPlacement(isInLowerHalf ? "above" : "below");
  }, [isShowCalendar, isLocationOpen]);

  /*******************
  @purpose : set start/end date once a range is picked
  @Parameter : {dateObj2}
  @Author : INIC
  ******************/
  const setStartEndDateOnChange = (dateObj2) => {
    if (
      dateObj2 &&
      Object.keys(dateObj2).length === 0 &&
      Object.getPrototypeOf(dateObj2) === Object.prototype
    ) {
      setDateObj(stateObj);
      setStartDate(null);
      setEndDate(null);
    } else {
      setDateObj([dateObj2]);
      setStartDate(format(dateObj2.startDate, "MMM d, yyyy", {}));
      setEndDate(format(dateObj2.endDate, "MMM d, yyyy", {}));
    }
  };

  /*******************
  @purpose : close the location popover on outside click
  @Author : INIC
  ******************/
  useEffect(() => {
    if (!isLocationOpen) return;

    const handleOutsideClick = (e) => {
      // Google Places renders its suggestion dropdown in a `.pac-container`
      // appended directly to <body>, outside the popover's DOM subtree.
      if (e.target.closest && e.target.closest(".pac-container")) return;

      if (
        locationFieldRef.current &&
        !locationFieldRef.current.contains(e.target)
      ) {
        closePopover();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isLocationOpen]);

  /*******************
  @purpose : close the calendar popover on outside click (desktop anchored
             popover only - on mobile the calendar is a full-screen sheet
             that already covers the whole viewport, so nothing outside it
             is reachable to click)
  @Author : INIC
  ******************/
  useEffect(() => {
    if (!isShowCalendar) return;

    const handleOutsideClick = (e) => {
      if (searchBarRef.current && !searchBarRef.current.contains(e.target)) {
        closePopover();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isShowCalendar]);

  /*******************
  @purpose : Next button handler - persist the picked date/time/location and
             go to the location-wide search results page
  @Author : INIC
  ******************/
  const handleNextClick = () => {
    if (currentLocationId) {
      set_cookie("currentLocationId", currentLocationId, 1);
    }
    set_cookie("daterange", `${startDate || ""}|${endDate || ""}`, 1);
    set_cookie("timerange", `${startTime || ""}|${endTime || ""}`, 1);
    set_cookie("selectedLocationDisplay", locationDisplay || "", 1);
    // Full draft (one-way, delivery/return address), not just the display
    // string - always written (even when unselected, clearing to "") so a
    // stale draft from an earlier visit can't leak into this fresh search.
    set_cookie(
      "selectedLocationDraft",
      savedLocationDraft ? JSON.stringify(savedLocationDraft) : "",
      1
    );

    // Also carried in the URL (not just the cookie) so the search-results
    // page can restore this exact search on back/forward navigation.
    const query = {};
    if (startDate) query.startDate = startDate;
    if (endDate) query.endDate = endDate;
    if (startTime) query.startTime = startTime;
    if (endTime) query.endTime = endTime;
    if (locationDisplay) query.location = locationDisplay;
    if (savedLocationDraft) query.locationDraft = JSON.stringify(savedLocationDraft);

    router.push({ pathname: "/search-results", query });
  };

  return (
      <div
        className="reservation-search-bar"
        ref={searchBarRef}
      >
        <div
          className="reservation-field date-field start-date-field"
          onClick={handleClickOpenCalendar}
        >
          <IconCalendar className="reservation-field-icon" />
          <span className={`field-label ${startDate ? "" : "field-label-placeholder"}`}>{startDate ? formatDisplayDate(startDate) : "Start date"}</span>
        </div>

        <div className="reservation-field time-field">
          <TimeDropdown
            value={startTime}
            onChange={setStartTime}
            options={startTimeOptions}
            isOpen={activePopover === "startTime"}
            onToggle={() => togglePopover("startTime")}
            onClose={closePopover}
          />
        </div>

        <div
          className="reservation-field date-field end-date-field"
          onClick={handleClickOpenCalendar}
        >
          <IconCalendar className="reservation-field-icon" />
          <span className={`field-label ${endDate ? "" : "field-label-placeholder"}`}>
            {endDate ? formatDisplayDate(endDate) : "End date"}
          </span>
        </div>

        <div className="reservation-field time-field">
          <TimeDropdown
            value={endTime}
            onChange={setEndTime}
            options={endTimeOptions}
            isOpen={activePopover === "endTime"}
            onToggle={() => togglePopover("endTime")}
            onClose={closePopover}
          />
        </div>

        <div className="reservation-field location-field" ref={locationFieldRef}>
          <div
            className="location-field-trigger"
            onClick={handleToggleLocation}
          >
            <IconLocationPin className="reservation-field-icon" />
            <span className={`field-label field-label-location ${isLocationSelected ? "" : "field-label-placeholder"}`}>{locationDisplay || LOCATION_PLACEHOLDER}</span>
          </div>

          {isLocationOpen && (
            <LocationPopover
              initialDraft={savedLocationDraft}
              placement={popoverPlacement}
              onClose={closePopover}
              onSave={(display, draft) => {
                setLocationDisplay(display || "Location");
                setSavedLocationDraft(draft);
                closePopover();
              }}
            />
          )}
        </div>

        <button
            type="button"
            className="reservation-next-btn"
            onClick={handleNextClick}
            disabled={!isFormComplete}
        >
          Next <IconArrowRight className="reservation-next-btn-icon" />
        </button>

        {isShowCalendar && (
          <DateRangePicker
            dateObj={dateObj}
            setDateObj={setDateObj}
            handleClickOpenCalendar={handleClickOpenCalendar}
            setStartEndDateOnChange={setStartEndDateOnChange}
            startTimeOptionsHandler={() => {}}
            setStartTimeDefaultValue={() => {}}
            setEndTimeDefaultValue={() => {}}
            tempDirections="horizontal"
            minimumDay={1}
            showMinimumErrorMessage={() => {}}
            selectedStartDate={startDate}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            isAvailable={false}
            placement={popoverPlacement}
          />
        )}
      </div>
  );
}

export default ReservationSearchBar;
